import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import interviewService from '../services/interviewService';
import ScheduleInterviewModal from './ScheduleInterviewModal';
import CompleteInterviewModal from './CompleteInterviewModal';
import CancelInterviewModal from './CancelInterviewModal';

const InterviewSection = ({ applicationId, applicationStage, onInterviewUpdate }) => {
  const { user } = useAuth();
  const [interview, setInterview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [preselectedEventType, setPreselectedEventType] = useState('interview');
  const [meetingLinkInput, setMeetingLinkInput] = useState('');
  const [meetingInstructionsInput, setMeetingInstructionsInput] = useState('');
  const [meetingSaveLoading, setMeetingSaveLoading] = useState(false);
  const [meetingMessage, setMeetingMessage] = useState('');
  const [availabilityInput, setAvailabilityInput] = useState('');
  const [nextAvailableAtInput, setNextAvailableAtInput] = useState('');
  const [availabilitySaveLoading, setAvailabilitySaveLoading] = useState(false);
  const [availabilityMessage, setAvailabilityMessage] = useState('');

  const isAfterVisaSectionStage = ['PRE_DEPARTURE', 'CLEARANCE', 'TICKET', 'DEPARTURE', 'COMPLETED'].includes(applicationStage);

  const getPreferredEventTypeByStage = () => {
    if (applicationStage === 'EXAM') return 'exam';
    if (applicationStage === 'PRE_DEPARTURE') return 'process';
    if (isAfterVisaSectionStage) return 'process';
    return null;
  };

  const getPrimaryScheduleType = () => {
    if (applicationStage === 'EXAM') return 'exam';
    if (applicationStage === 'PRE_DEPARTURE') return 'process';
    if (isAfterVisaSectionStage) return 'process';
    return 'interview';
  };

  useEffect(() => {
    fetchInterview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId, applicationStage]);

  const fetchInterview = async () => {
    setLoading(true);
    try {
      const preferredEventType = getPreferredEventTypeByStage();
      const response = await interviewService.getApplicationInterview(applicationId, preferredEventType);

      setInterview(response || null);
      setMeetingLinkInput(response?.meetingLink || '');
      setMeetingInstructionsInput(response?.meetingInstructions || '');
      setAvailabilityInput(response?.candidateAvailabilityStatus || '');
      setNextAvailableAtInput(
        response?.candidateNextAvailableAt ? toLocalDateTimeInputValue(response.candidateNextAvailableAt) : ''
      );
      setAvailabilityMessage('');
    } catch (err) {
      console.error('Failed to load interview:', err);
      setInterview(null);
      setMeetingLinkInput('');
      setMeetingInstructionsInput('');
      setAvailabilityInput('');
      setNextAvailableAtInput('');
      setAvailabilityMessage('');
    } finally {
      setLoading(false);
    }
  };

  const openScheduleModal = (eventType = 'interview') => {
    setPreselectedEventType(eventType);
    setShowScheduleModal(true);
  };

  const handleScheduleSuccess = () => {
    setShowScheduleModal(false);
    setPreselectedEventType('interview');
    fetchInterview();
    if (onInterviewUpdate) onInterviewUpdate();
  };

  const handleCompleteSuccess = (outcome) => {
    setShowCompleteModal(false);
    fetchInterview();
    if (onInterviewUpdate) onInterviewUpdate();
    if (outcome === 'PASSED') {
      alert('Interview marked PASSED. Application moved to the next stage automatically.');
      return;
    }

    alert(`Interview completed with outcome: ${outcome}`);
  };

  const handleCancelSuccess = () => {
    setShowCancelModal(false);
    fetchInterview();
    if (onInterviewUpdate) onInterviewUpdate();
  };

  const handleSaveMeetingDetails = async () => {
    if (!interview?._id) return;

    // Meeting link is now optional - no validation required

    setMeetingSaveLoading(true);
    setMeetingMessage('');

    try {
      const response = await interviewService.updateMeetingDetails(interview._id, {
        meetingLink: meetingLinkInput.trim(),
        meetingInstructions: meetingInstructionsInput.trim()
      });

      if (response.success) {
        setMeetingMessage('Meeting details updated successfully.');
        fetchInterview();
      }
    } catch (err) {
      setMeetingMessage(err.response?.data?.error || 'Failed to update meeting details');
    } finally {
      setMeetingSaveLoading(false);
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const toLocalDateTimeInputValue = (dateString) => {
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    const pad = (value) => String(value).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const eventType = (interview?.eventType || 'interview').toLowerCase();
  const eventLabel = eventType === 'exam' ? 'Exam' : eventType === 'process' ? 'Pre-Departure' : 'Interview';
  const eventDateLabel = eventType === 'exam' ? 'exam date' : eventType === 'process' ? 'pre-departure date' : 'interview date';

  const handleSaveAvailability = async () => {
    if (!interview?._id) return;

    if (!availabilityInput) {
      setAvailabilityMessage(`Please select whether you are available for this ${eventDateLabel}.`);
      return;
    }

    if (availabilityInput === 'NOT_AVAILABLE' && !nextAvailableAtInput) {
      setAvailabilityMessage(`Please provide your next available date and time for ${eventLabel}.`);
      return;
    }

    setAvailabilitySaveLoading(true);
    setAvailabilityMessage('');

    try {
      const payload = {
        isAvailable: availabilityInput === 'AVAILABLE'
      };

      if (availabilityInput === 'NOT_AVAILABLE') {
        payload.nextAvailableAt = new Date(nextAvailableAtInput).toISOString();
      }

      const response = await interviewService.updateCandidateAvailability(interview._id, payload);
      if (response.success) {
        setAvailabilityMessage(`Your ${eventLabel.toLowerCase()} availability response has been saved.`);
        fetchInterview();
      }
    } catch (err) {
      setAvailabilityMessage(err.response?.data?.error || 'Failed to save availability response');
    } finally {
      setAvailabilitySaveLoading(false);
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'SCHEDULED': return 'bg-[#d4edda] text-[#155724]';
      case 'COMPLETED': return 'bg-[#cce5ff] text-[#004085]';
      case 'CANCELLED': return 'bg-[#f8d7da] text-[#721c24]';
      case 'NO_SHOW': return 'bg-[#fff3cd] text-[#856404]';
      default: return '';
    }
  };

  const getOutcomeClass = (outcome) => {
    switch (outcome) {
      case 'PASSED': return 'bg-[#28a745] text-white';
      case 'FAILED': return 'bg-[#dc3545] text-white';
      default: return '';
    }
  };

  const canSchedule = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role);
  const canAcknowledgeAvailability = user?.role === 'CANDIDATE' && interview?.status === 'SCHEDULED';
  const primaryScheduleType = getPrimaryScheduleType();
  const primaryScheduleLabel = primaryScheduleType === 'exam' ? 'Exam' : primaryScheduleType === 'process' ? 'Pre-Departure Orientation Training' : 'Interview';
  const canComplete = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role) && interview?.status === 'SCHEDULED';
  const canCancel = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role) && interview?.status === 'SCHEDULED';
  const canEditMeetingDetails = ['HR', 'ADMIN', 'CHAIRMAN'].includes(user?.role) && interview?.status === 'SCHEDULED';
  const canScheduleExam = canSchedule && (
    applicationStage === 'EXAM' ||
    (interview?.status === 'COMPLETED' && eventType === 'interview')
  );
  const canScheduleProcess = canSchedule && (
    isAfterVisaSectionStage ||
    (interview?.status === 'COMPLETED' && eventType === 'interview')
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg p-6 shadow-[0_2px_4px_rgba(0,0,0,0.1)] mt-6">
        <h3>{primaryScheduleLabel}</h3>
        <div className="text-center py-10 text-[#6c757d]">Loading event information...</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-6 shadow-[0_2px_4px_rgba(0,0,0,0.1)] mt-6">
      <div className="flex justify-between items-center mb-5 md:flex-col md:items-start md:gap-3">
        <h3 className="m-0 text-[#2c3e50] text-xl">{primaryScheduleLabel}</h3>
        {(!interview || interview.status === 'CANCELLED') && canSchedule && (
          <button
            className="px-5 py-2.5 border-none rounded-md text-[0.95rem] font-medium cursor-pointer transition-all duration-200 bg-[#007bff] text-white hover:bg-[#0056b3]"
            onClick={() => openScheduleModal(primaryScheduleType)}
          >
            {interview?.status === 'CANCELLED' ? `Reschedule ${eventLabel}` : `Schedule ${primaryScheduleLabel}`}
          </button>
        )}
      </div>

      {interview ? (
        <div className="bg-[#f8f9fa] rounded-md p-5 border-l-4 border-[#007bff]">
          <div className="flex flex-col gap-3 mb-4">
            <div className="flex gap-3 items-start md:flex-col md:gap-1">
              <span className="font-semibold text-[#495057] min-w-[120px] md:min-w-0">Scheduled:</span>
              <span className="text-[#2c3e50] flex-1">{formatDateTime(interview.scheduledAt)}</span>
            </div>
            <div className="flex gap-3 items-start md:flex-col md:gap-1">
              <span className="font-semibold text-[#495057] min-w-[120px] md:min-w-0">Status:</span>
              <span className={`px-3 py-1 rounded-xl text-[0.75rem] font-semibold uppercase ${getStatusClass(interview.status)}`}>
                {interview.status}
              </span>
            </div>
            {interview.outcome !== 'PENDING' && (
              <div className="flex gap-3 items-start md:flex-col md:gap-1">
                <span className="font-semibold text-[#495057] min-w-[120px] md:min-w-0">Outcome:</span>
                <span className={`px-3 py-1 rounded-xl text-[0.75rem] font-semibold uppercase ${getOutcomeClass(interview.outcome)}`}>
                  {interview.outcome}
                </span>
              </div>
            )}
            {interview.notes && (
              <div className="flex gap-3 items-start md:flex-col md:gap-1">
                <span className="font-semibold text-[#495057] min-w-[120px] md:min-w-0">Notes:</span>
                <span className="text-[#2c3e50] flex-1">{interview.notes}</span>
              </div>
            )}
            {interview.title && (
              <div className="flex gap-3 items-start md:flex-col md:gap-1">
                <span className="font-semibold text-[#495057] min-w-[120px] md:min-w-0">Title:</span>
                <span className="text-[#2c3e50] flex-1">{interview.title}</span>
              </div>
            )}
            {interview.description && (
              <div className="flex gap-3 items-start md:flex-col md:gap-1">
                <span className="font-semibold text-[#495057] min-w-[120px] md:min-w-0">Description:</span>
                <span className="text-[#2c3e50] flex-1">{interview.description}</span>
              </div>
            )}
            {interview.candidateAvailabilityStatus && (
              <div className="mt-1 p-3 border border-[#dee2e6] rounded-md bg-white">
                <div className="font-semibold text-[#2c3e50] mb-2">Candidate Availability</div>
                <div className="text-sm mb-1">
                  <span className="font-semibold text-[#495057] mr-2">Response:</span>
                  <span className="text-[#2c3e50]">
                    {interview.candidateAvailabilityStatus === 'AVAILABLE'
                      ? `Available for scheduled ${eventDateLabel}`
                      : `Not available for scheduled ${eventDateLabel}`}
                  </span>
                </div>
                {interview.candidateAvailabilityStatus === 'NOT_AVAILABLE' && interview.candidateNextAvailableAt && (
                  <div className="text-sm mb-1">
                    <span className="font-semibold text-[#495057] mr-2">Next Available:</span>
                    <span className="text-[#2c3e50]">{formatDateTime(interview.candidateNextAvailableAt)}</span>
                  </div>
                )}
                {interview.candidateAvailabilityAcknowledgedAt && (
                  <div className="text-sm">
                    <span className="font-semibold text-[#495057] mr-2">Acknowledged At:</span>
                    <span className="text-[#2c3e50]">{formatDateTime(interview.candidateAvailabilityAcknowledgedAt)}</span>
                  </div>
                )}
              </div>
            )}
            {(interview.meetingLink || canEditMeetingDetails) && (
              <div className="mt-2 p-3 border border-[#dee2e6] rounded-md bg-white">
                <div className="font-semibold text-[#2c3e50] mb-2">Meeting Details</div>
                {interview.meetingLink && (
                  <div className="text-sm mb-2">
                    <span className="font-semibold text-[#495057] mr-2">Meeting Link:</span>
                    <a
                      href={interview.meetingLink}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[#007bff] break-all"
                    >
                      {interview.meetingLink}
                    </a>
                  </div>
                )}
                {interview.meetingInstructions && (
                  <div className="text-sm mb-2">
                    <span className="font-semibold text-[#495057] mr-2">Instructions:</span>
                    <span className="text-[#2c3e50]">{interview.meetingInstructions}</span>
                  </div>
                )}

                {canEditMeetingDetails && (
                  <div className="mt-3 border-t border-[#e9ecef] pt-3">
                    <div className="text-xs text-[#6c757d] mb-2">Edit Meeting Details</div>
                    <input
                      type="url"
                      value={meetingLinkInput}
                      onChange={(e) => setMeetingLinkInput(e.target.value)}
                      placeholder="https://meet.google.com/..."
                      className="w-full px-3 py-2 border border-[#ced4da] rounded-md mb-2"
                      disabled={meetingSaveLoading}
                    />
                    <textarea
                      value={meetingInstructionsInput}
                      onChange={(e) => setMeetingInstructionsInput(e.target.value)}
                      placeholder="Meeting instructions for candidate"
                      rows={2}
                      className="w-full px-3 py-2 border border-[#ced4da] rounded-md mb-2"
                      disabled={meetingSaveLoading}
                    />
                    <button
                      className="px-4 py-2 border-none rounded-md text-sm font-medium cursor-pointer transition-all duration-200 bg-[#007bff] text-white hover:bg-[#0056b3] disabled:opacity-60"
                      onClick={handleSaveMeetingDetails}
                      disabled={meetingSaveLoading}
                      type="button"
                    >
                      {meetingSaveLoading ? 'Saving...' : 'Save Meeting Details'}
                    </button>
                    {meetingMessage && (
                      <div className="text-xs mt-2 text-[#495057]">{meetingMessage}</div>
                    )}
                  </div>
                )}
              </div>
            )}
            {canAcknowledgeAvailability && (
              <div className="mt-2 p-3 border border-[#dee2e6] rounded-md bg-white">
                <div className="font-semibold text-[#2c3e50] mb-2">{eventLabel} Availability Confirmation</div>
                <p className="m-0 mb-3 text-sm text-[#495057]">
                  Please confirm if you are available on the scheduled {eventDateLabel}.
                </p>

                <div className="flex flex-col gap-2 mb-3">
                  <label className="inline-flex items-center gap-2 text-sm text-[#2c3e50] cursor-pointer">
                    <input
                      type="radio"
                      name="candidateAvailability"
                      value="AVAILABLE"
                      checked={availabilityInput === 'AVAILABLE'}
                      onChange={(e) => setAvailabilityInput(e.target.value)}
                      disabled={availabilitySaveLoading}
                    />
                    Yes, I am available on this {eventDateLabel}
                  </label>
                  <label className="inline-flex items-center gap-2 text-sm text-[#2c3e50] cursor-pointer">
                    <input
                      type="radio"
                      name="candidateAvailability"
                      value="NOT_AVAILABLE"
                      checked={availabilityInput === 'NOT_AVAILABLE'}
                      onChange={(e) => setAvailabilityInput(e.target.value)}
                      disabled={availabilitySaveLoading}
                    />
                    No, I am not available on this {eventDateLabel}
                  </label>
                </div>

                {availabilityInput === 'NOT_AVAILABLE' && (
                  <div className="mb-3">
                    <label className="block text-xs text-[#6c757d] mb-1">When will you be available for {eventLabel}?</label>
                    <input
                      type="datetime-local"
                      value={nextAvailableAtInput}
                      onChange={(e) => setNextAvailableAtInput(e.target.value)}
                      className="w-full px-3 py-2 border border-[#ced4da] rounded-md"
                      disabled={availabilitySaveLoading}
                    />
                  </div>
                )}

                <button
                  className="px-4 py-2 border-none rounded-md text-sm font-medium cursor-pointer transition-all duration-200 bg-[#007bff] text-white hover:bg-[#0056b3] disabled:opacity-60"
                  onClick={handleSaveAvailability}
                  disabled={availabilitySaveLoading}
                  type="button"
                >
                  {availabilitySaveLoading ? 'Saving...' : 'Save Availability'}
                </button>

                {availabilityMessage && (
                  <div className="text-xs mt-2 text-[#495057]">{availabilityMessage}</div>
                )}
              </div>
            )}
            {interview.cancellationReason && (
              <div className="flex gap-3 items-start md:flex-col md:gap-1">
                <span className="font-semibold text-[#495057] min-w-[120px] md:min-w-0">Cancellation Reason:</span>
                <span className="text-[#2c3e50] flex-1">{interview.cancellationReason}</span>
              </div>
            )}
          </div>

          {(canComplete || canCancel || (interview.status === 'CANCELLED' && canSchedule) || canScheduleExam || canScheduleProcess) && (
            <div className="flex gap-3 pt-4 border-t border-[#dee2e6] md:flex-col md:[&>button]:w-full">
              {canComplete && (
                <button className="px-5 py-2.5 border-none rounded-md text-[0.95rem] font-medium cursor-pointer transition-all duration-200 bg-[#28a745] text-white hover:bg-[#218838]" onClick={() => setShowCompleteModal(true)}>
                  Complete {eventLabel}
                </button>
              )}
              {canCancel && (
                <button className="px-5 py-2.5 border-none rounded-md text-[0.95rem] font-medium cursor-pointer transition-all duration-200 bg-[#dc3545] text-white hover:bg-[#c82333]" onClick={() => setShowCancelModal(true)}>
                  Cancel {eventLabel}
                </button>
              )}
              {interview.status === 'CANCELLED' && canSchedule && (
                <button className="px-5 py-2.5 border-none rounded-md text-[0.95rem] font-medium cursor-pointer transition-all duration-200 bg-[#007bff] text-white hover:bg-[#0056b3]" onClick={() => openScheduleModal(primaryScheduleType)}>
                  Reschedule {eventLabel}
                </button>
              )}
              {canScheduleExam && (
                <button className="px-5 py-2.5 border-none rounded-md text-[0.95rem] font-medium cursor-pointer transition-all duration-200 bg-[#dc3545] text-white hover:bg-[#c82333]" onClick={() => openScheduleModal('exam')}>
                  Schedule Exam
                </button>
              )}
              {canScheduleProcess && (
                <button className="px-5 py-2.5 border-none rounded-md text-[0.95rem] font-medium cursor-pointer transition-all duration-200 bg-[#28a745] text-white hover:bg-[#218838]" onClick={() => openScheduleModal('process')}>
                  Schedule Pre-Departure
                </button>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-10 text-[#6c757d] italic">
          <p>No event scheduled yet</p>
        </div>
      )}

      {showScheduleModal && (
        <ScheduleInterviewModal
          preselectedApplicationId={applicationId}
          preselectedEventType={preselectedEventType}
          onClose={() => setShowScheduleModal(false)}
          onSuccess={handleScheduleSuccess}
        />
      )}

      {showCompleteModal && interview && (
        <CompleteInterviewModal
          interview={interview}
          onClose={() => setShowCompleteModal(false)}
          onSuccess={handleCompleteSuccess}
        />
      )}

      {showCancelModal && interview && (
        <CancelInterviewModal
          interview={interview}
          onClose={() => setShowCancelModal(false)}
          onSuccess={handleCancelSuccess}
        />
      )}
    </div>
  );
};

export default InterviewSection;

